/* eslint-disable */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useCart } from '../../context/CartContext';
import { useSelectedCurrency, usePricingService, useCurrency } from '../../hooks';
import { DEFAULT_CURRENCY, ORDER_LIMITS } from '@/constants';
import { useOrdersService } from '../../hooks/useOrdersService';
import { CartService } from '../../api/services/cartService';
import { PaymentTilesService } from '../../api/services/paymentTilesService';
import { ApiClient } from '../../api/apiClient';
import { useRouter, usePathname } from 'next/navigation';
import { Card, findCardVariant } from '../../types/card';
import { PAYMENT_TILES } from '../../types/order';
import type { CryptoPayOption, PaymentTile, PaymentTileSwitches } from '../../types/order';
import { getLocalizedPath } from '../../i18n/pathnames';
import { locales, defaultLocale, type Locale } from '../../i18n/config';
import { GaEcommerceEvent, GaEcommerceItem, getGaClientId, getGaSessionId, getGclid, getFbclid, trackGaEcommerce } from '../GoogleAnalytics/GoogleAnalytics';
import { getFbp, getFbc, trackInitiateCheckout } from '../MetaPixel/MetaPixel';
import { getPosthogDistinctId, identifyPosthogUser } from '../../lib/posthog-utils';
import { getStoredAttribution } from '../../lib/attribution';
import { useAuth } from '../../context/AuthContext';
import type { OtpChallenge } from './OtpStepUpModal';
import type { KycChallenge } from './KycStepUpModal';
import { KycStatus } from '../../types/kyc';
import { useCookieConsent } from '../../context/CookieConsentContext';
import { getUserEmail } from '../../utils/getUserEmail';
import { useTurnstile } from '../../hooks/useTurnstile';
import { useEmailCheck } from './useEmailCheck';


const NO_TILE_SWITCHES: PaymentTileSwitches = { unavailable: [], hidden: [] };

// Helper type for cart items enriched with card data
export type CartItemWithCard = {
  productId: string;
  quantity: number;
  selectedValue: number;
  currency: string;
  country?: string;
  variantCountries?: string[];
  card?: Card;
};

export const useCartLogic = () => {
  console.log('🚀 Cart component render started');
  
  // Add client-side mounting check to prevent hydration issues
  const [isMounted, setIsMounted] = useState(false);
  
  const { cart, cards, cartId, removeFromCart, updateItemQuantity, clearCart, getTotalPrice, isLoading, adoptCart, error: cartError, errorCode: cartErrorCode, clearError: clearCartError } = useCart();
  console.log('📦 Cart hook data:', { cart: !!cart, cardsCount: cards?.length, cartId, isLoading });
  
  const { selectedCurrency } = useSelectedCurrency();
  console.log('💰 Selected currency:', selectedCurrency);
  
  const { getCardsPricing } = usePricingService();
  const { getRate } = useCurrency();
  console.log('🔧 Pricing service loaded');
  
  // Initialize cart servicex
  const cartService = useMemo(() => new CartService(new ApiClient()), []);
  const paymentTilesService = useMemo(() => new PaymentTilesService(new ApiClient()), []);
  
  // State for cart pricing and coupon
  const [cartPricing, setCartPricing] = useState<any>(null);
  const [couponCode, setCouponCode] = useState('');
  const [isLoadingPricing, setIsLoadingPricing] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [pricingError, setPricingError] = useState('');
  // State for cart items without individual pricing
  const [cartItems, setCartItems] = useState<CartItemWithCard[]>([]);
  const [minOrderError, setMinOrderError] = useState<{ min: number; current: number } | null>(null);
  
  
  const validateMinimumOrder = useCallback(async (total: number, currency: string): Promise<boolean> => {
    if (!total || total <= 0) {
      setMinOrderError(null);
      return false;
    }

    let totalInUsd = total;

    if (currency !== DEFAULT_CURRENCY) {
      try {
        const rate = await getRate(currency, DEFAULT_CURRENCY);
        totalInUsd = total * rate.rate;
      } catch (error) {
        setMinOrderError(null);
        return true;
      }
    }

    if (totalInUsd < ORDER_LIMITS.minOrderUsd) {
      setMinOrderError({ min: ORDER_LIMITS.minOrderUsd, current: totalInUsd });
      return false;
    }

    setMinOrderError(null);
    return true;
  }, [getRate]);
  
  // Memoized cart items creation to prevent infinite re-renders
  const memoizedCartItems = useMemo((): CartItemWithCard[] => {
    console.log('🔄 useMemo: createCartItems called');
    console.log('📦 Cart products:', cart?.products?.length);
    console.log('🃏 Available cards:', cards?.length);
    
    if (!cart?.products || !cards?.length) {
      console.log('❌ No cart products or cards found');
      return [];
    }
    
    const cardsMap = new Map(cards.map(card => [card._id, card]));
    console.log('🗺️ Cards map created:', cardsMap.size, 'cards');
    
    const result = cart.products.map(item => {
      const card = cardsMap.get(item.productId);
      const variant = card
        ? findCardVariant(card, item.selectedValue, item.currency, item.country)
        : undefined;
      return {
        productId: item.productId,
        quantity: item.quantity,
        selectedValue: item.selectedValue,
        currency: item.currency,
        country: item.country,
        variantCountries: variant?.countries ?? card?.countries,
        card,
      };
    });
    
    console.log('✅ Cart items created:', result.length, 'items');
    return result;
  }, [cart?.products, cards]);
  
  // Update state when memoized items change
  useEffect(() => {
    console.log('🔄 useEffect: Setting cart items from memo');
    setCartItems(memoizedCartItems);
  }, [memoizedCartItems]);

  // Fetch cart pricing without coupon (for initial load and quantity changes)
  const fetchBasicCartPricing = async () => {
    console.log('💰 fetchBasicCartPricing called');
    console.log('📊 Cart items length:', cartItems.length);
    if (!cartItems.length) {
      console.log('❌ No cart items, skipping pricing fetch');
      return;
    }
    console.log('🔄 Starting pricing fetch...');
    setIsLoadingPricing(true);
    setPricingError('');
    try {
      const cards = cartItems.flatMap(item =>
        Array(item.quantity).fill({
          id: item.productId,
          faceValue: item.selectedValue,
          currency: item.currency,
          country: item.country
        })
      );
      console.log('🃏 Cards for pricing:', cards);
      const query = {
        cards,
        desiredCurrency: selectedCurrency
      };
      console.log('📝 Pricing query:', query);
      console.log('🔧 Calling getCardsPricing...');
      const result = await getCardsPricing(query);
      console.log('📊 Pricing result:', result);
      if (result) {
        const pricing = {
          ...result,
          savings: result.totalNoDiscount - result.total,
          itemCount: cards.length,
          couponApplied: false
        };
        console.log('✅ Setting cart pricing:', pricing);
        setCartPricing(pricing);
        await validateMinimumOrder(result.total, result.currency);
      } else {
        console.log('❌ No pricing result received');
      }
    } catch (error: any) {
      console.error('💥 Error fetching basic cart pricing:', error);
      console.error('📊 Error details:', {
        status: error?.response?.status,
        message: error?.response?.data?.message,
        error: error?.message
      });
      if (error?.response?.status === 400 && error?.response?.data?.message?.includes('Currency') && error?.response?.data?.message?.includes('not supported')) {
        console.log('❌ Currency not supported error');
        setPricingError('One of the currencies is not currently supported.');
      } else {
        console.log('❌ Generic pricing error');
        setPricingError('Unable to calculate pricing. Please try again.');
      }
      setCartPricing(null);
    } finally {
      console.log('🏁 Pricing fetch completed');
      setIsLoadingPricing(false);
    }
  };

  // Apply coupon when user clicks Apply button
  const handleApplyCoupon = async () => {
    console.log('🎫 handleApplyCoupon called');
    console.log('📊 Cart items length:', cartItems.length);
    console.log('🎫 Coupon code:', couponCode);
    if (!cartItems.length || !couponCode.trim()) {
      console.log('❌ No cart items or coupon code, skipping');
      return;
    }
    console.log('🔄 Starting coupon application...');
    setIsLoadingPricing(true);
    setCouponError('');
    try {
      const cards = cartItems.flatMap(item =>
        Array(item.quantity).fill({
          id: item.productId,
          faceValue: item.selectedValue,
          currency: item.currency,
          country: item.country
        })
      );
      console.log('🃏 Cards for coupon pricing:', cards);
      const query = {
        cards,
        couponCode: couponCode,
        desiredCurrency: selectedCurrency
      };
      console.log('📝 Coupon query:', query);
      console.log('🔧 Calling getCardsPricing with coupon...');
      const result = await getCardsPricing(query);
      console.log('📊 Coupon pricing result:', result);
      if (result) {
        const pricing = {
          ...result,
          savings: result.totalNoDiscount - result.total,
          itemCount: cards.length,
          couponApplied: true
        };
        console.log('✅ Setting coupon pricing:', pricing);
        setCartPricing(pricing);
        setPricingError('');
        await validateMinimumOrder(result.total, result.currency);
      } else {
        console.log('❌ No coupon pricing result received');
        setCouponError('Unable to apply coupon code. Please check the code and try again.');
      }
    } catch (error: any) {
      console.error('💥 Error applying coupon:', error);
      console.error('📊 Coupon error details:', {
        status: error?.response?.status,
        message: error?.response?.data?.message,
        error: error?.message
      });
      if (error?.response?.status === 400 && error?.response?.data?.message?.includes('Currency') && error?.response?.data?.message?.includes('not supported')) {
        console.log('❌ Currency not supported error in coupon');
        setPricingError('One of the currencies is not currently supported.');
        setCartPricing(null);
      } else {
        if (error?.response?.data?.message) {
          console.log('❌ API error message:', error.response.data.message);
          setCouponError(error.response.data.message);
        } else if (error?.message) {
          console.log('❌ Generic error message:', error.message);
          setCouponError(error.message);
        } else {
          console.log('❌ Unknown coupon error');
          setCouponError('Unable to apply coupon code. Please check the code and try again.');
        }
      }
    } finally {
      console.log('🏁 Coupon application completed');
      setIsLoadingPricing(false);
    }
  };
  
  // Fetch pricing when cart items change (without coupon)
  useEffect(() => {
    console.log('🔄 useEffect: fetchPricing triggered');
    console.log('📊 Cart items length:', cartItems.length);
    console.log('🔧 Is mounted:', isMounted);
    if (!isMounted) {
      console.log('❌ Component not mounted yet, skipping pricing fetch');
      return;
    }
    if (cartItems.length > 0) {
      console.log('✅ Cart has items, calling fetchBasicCartPricing');
      fetchBasicCartPricing();
    } else {
      console.log('❌ No cart items, skipping pricing fetch');
    }
  }, [cartItems, selectedCurrency, isMounted]);

  // GA4 items for view_cart / begin_checkout. Same lookup as CartItem.tsx:
  // prices[] holds one entry per card in cartPricing.currency; until pricing
  // answers, the unit price in that currency is unknown and left out.
  const gaCartItems = useCallback((): GaEcommerceItem[] => {
    let priceIndex = 0;
    return cartItems.map((item) => {
      const unitPrice = cartPricing?.prices?.[priceIndex]?.price;
      priceIndex += item.quantity;
      return {
        item_id: item.productId,
        item_name: item.card?.name ?? item.productId,
        item_variant: `${item.selectedValue} ${item.currency}`,
        price: unitPrice,
        quantity: item.quantity,
      };
    });
  }, [cartItems, cartPricing]);

  // Once per cart visit, as soon as the priced total is known.
  const viewCartFiredRef = useRef(false);
  useEffect(() => {
    if (viewCartFiredRef.current || !cartPricing || !cartItems.length) return;
    viewCartFiredRef.current = true;
    trackGaEcommerce(GaEcommerceEvent.ViewCart, cartPricing.currency, cartPricing.total, gaCartItems());
  }, [cartPricing, cartItems, gaCartItems]);
  
  // Initialize with empty string and update from localStorage in useEffect
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSaved, setEmailSaved] = useState(false);
  // The single required checkout consent: country/region restrictions check
  // plus the express request that we start performing immediately, which
  // forfeits the statutory 14-day withdrawal right. Never pre-ticked, and
  // CartSummary blocks checkout until it is set - see Terms IV. Right of
  // Withdrawal.
  const [expressConsent, setExpressConsent] = useState(false);
  const [subscribeToNewsletter, setSubscribeToNewsletter] = useState(false);
  const [buyAsGift, setBuyAsGift] = useState(false);
  const [giftEmail, setGiftEmail] = useState('');
  const [giftEmailError, setGiftEmailError] = useState('');
  const [giftMessage, setGiftMessage] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'crypto' | 'bank_transfer'>('crypto');
  // Which crypto tile is selected (classic | stablecoin). Only sent while
  // paymentMethod is crypto; the server maps it to a provider.
  const [payOption, setPayOption] = useState<CryptoPayOption>('classic');
  // Risk-band step-up challenge (OTP modal); null = no modal.
  const [otpChallenge, setOtpChallenge] = useState<OtpChallenge | null>(null);
  // Identity-verification step-up (KYC modal); null = no modal.
  const [kycChallenge, setKycChallenge] = useState<KycChallenge | null>(null);
  // An amount rule of the risk engine demanded a verification step the guest
  // cannot pass (RISK_ORDER_LOGIN_REQUIRED): the cart shows a login notice.
  const [amountRuleLoginRequired, setAmountRuleLoginRequired] = useState(false);
  // Set when the OTP modal finishes: the retry waits for a fresh turnstile
  // token (the challenged attempt consumed the previous one).
  const [pendingOtpRetry, setPendingOtpRetry] = useState(false);
  const [balanceApplyCents, setBalanceApplyCents] = useState<number>(0);
  // SEPA payer details: the saved bank account funding the transfer (the
  // server rejects bank_transfer orders with an external residual without
  // one) + a recoverable payer-gate error surfaced at the panel instead of
  // routing to /order/declined.
  const [payerBankAccountId, setPayerBankAccountIdState] = useState<string | null>(null);
  const [payerGateError, setPayerGateError] = useState<unknown>(null);
  // Reported up from BalancePanel (via CartSummary): applied balance covers
  // the whole order - no external payment, so no payer details needed.
  const [balanceFullyCovers, setBalanceFullyCovers] = useState(false);
  const { createOrder, isLoading: orderLoading } = useOrdersService();
  const { user, profile, balanceCents, refreshBalance, refreshProfile, isLoading: authLoading } = useAuth();
  const { categories: consentCategories } = useCookieConsent();
  const router = useRouter();
  const turnstile = useTurnstile({ action: 'checkout' });
  
  // Set mounted state to prevent hydration issues
  useEffect(() => {
    console.log('🔧 Setting mounted state');
    setIsMounted(true);
  }, []);
  
  useEffect(() => {
    if (!isMounted) return;

    const email = user?.email ?? getUserEmail();
    if (email) {
      setEmail(email);
    }
  }, [isMounted, user?.email]);

  // Bank transfer is logged-in-only (the backend rejects it for guests).
  // If the session ends while it's selected, fall back to crypto instead of
  // letting the checkout fail server-side.
  useEffect(() => {
    if (isMounted && !user && paymentMethod === 'bank_transfer') {
      setPaymentMethod('crypto');
    }
  }, [isMounted, user, paymentMethod]);

  // The amount-rule login notice belongs to the tile and the session it was
  // raised for: a login or a tile change clears it.
  useEffect(() => {
    setAmountRuleLoginRequired(false);
  }, [user, paymentMethod, payOption]);

  // A payer-gate error only makes sense while SEPA is selected.
  useEffect(() => {
    if (paymentMethod !== 'bank_transfer') {
      setPayerGateError(null);
    }
  }, [paymentMethod]);

  const setPayerBankAccountId = useCallback((id: string | null) => {
    setPayerBankAccountIdState(id);
    setPayerGateError(null);
  }, []);

  // Products can be flagged in admin as buyable by signed-in customers only.
  // Guests keep such items in the cart, but no payment method is offered -
  // the backend rejects the checkout with GIFT_PRODUCT_LOGIN_REQUIRED.
  const authRequiredCards = useMemo(() => {
    const seen = new Set<string>();
    return cartItems.reduce<Card[]>((acc, item) => {
      const card = item.card;
      if (card?.requiresAuth && !seen.has(card._id)) {
        seen.add(card._id);
        acc.push(card);
      }
      return acc;
    }, []);
  }, [cartItems]);

  // Wait for the session probe to settle - otherwise a signed-in customer
  // sees the "please log in" state flash before GET /user resolves.
  const authRequiredBlocked =
    isMounted && !authLoading && !user && authRequiredCards.length > 0;

  // A product can be kept away from single payment providers (admin flag or
  // keyword settings). The API names, per product, the tiles the cart must
  // not offer; the cart blocks all of them. The backend rejects a blocked
  // tile with GIFT_PAYMENT_METHOD_NOT_ALLOWED.
  const paymentRestrictedCards = useMemo(() => {
    const seen = new Set<string>();
    return cartItems.reduce<Card[]>((acc, item) => {
      const card = item.card;
      if (card?.blockedPaymentTiles?.length && !seen.has(card._id)) {
        seen.add(card._id);
        acc.push(card);
      }
      return acc;
    }, []);
  }, [cartItems]);

  const blockedPaymentTiles = useMemo(
    () => PAYMENT_TILES.filter((tile) =>
      paymentRestrictedCards.some((card) => card.blockedPaymentTiles?.includes(tile)),
    ),
    [paymentRestrictedCards],
  );

  // Tiles an admin switched off for everyone. A failed read leaves every
  // tile on; the API refuses a switched-off one with
  // GIFT_PAYMENT_METHOD_UNAVAILABLE.
  const [tileSwitches, setTileSwitches] = useState<PaymentTileSwitches>(NO_TILE_SWITCHES);
  // The tiles are drawn once the read has ended, well or badly: a hidden
  // tile must not show up for a moment and then go away.
  const [paymentTilesReady, setPaymentTilesReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    paymentTilesService
      .getSwitches()
      .then((switches) => {
        if (cancelled) return;
        setTileSwitches({
          unavailable: switches?.unavailable ?? [],
          hidden: switches?.hidden ?? [],
        });
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setPaymentTilesReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [paymentTilesService]);

  // Tiles this cart cannot be paid with: a product blocks them, or an admin
  // switched them off.
  const closedPaymentTiles = useMemo(
    () => PAYMENT_TILES.filter((tile) =>
      blockedPaymentTiles.includes(tile) ||
      tileSwitches.unavailable.includes(tile) ||
      tileSwitches.hidden.includes(tile),
    ),
    [blockedPaymentTiles, tileSwitches],
  );

  const selectedPaymentTile: PaymentTile =
    paymentMethod === 'bank_transfer' ? 'bank_transfer' : payOption;

  // Tiles the shopper can pick now. Bank transfer is not open to a guest
  // yet: it needs an account.
  const openPaymentTiles = useMemo(
    () => PAYMENT_TILES.filter((tile) =>
      !closedPaymentTiles.includes(tile) && (tile !== 'bank_transfer' || !!user),
    ),
    [closedPaymentTiles, user],
  );

  // The selection (the default one, or one made before the product came
  // into the cart) moves to the first tile the cart still offers.
  useEffect(() => {
    if (!isMounted || authLoading) return;
    if (!closedPaymentTiles.includes(selectedPaymentTile)) return;
    const next = openPaymentTiles[0];
    if (!next) return;
    if (next === 'bank_transfer') {
      setPaymentMethod('bank_transfer');
    } else {
      setPaymentMethod('crypto');
      setPayOption(next);
    }
  }, [isMounted, authLoading, closedPaymentTiles, openPaymentTiles, selectedPaymentTile]);

  // No tile is open for this cart. An order that balance pays in full needs
  // no tile, so it stays possible.
  const noPaymentTileOpen =
    isMounted &&
    !authLoading &&
    openPaymentTiles.length === 0 &&
    !balanceFullyCovers;

  // Bank transfer is left, and the guest has to log in for it.
  const paymentNeedsLogin =
    noPaymentTileOpen && !closedPaymentTiles.includes('bank_transfer');

  // Nothing is left, logged in or not.
  const noPaymentPossible =
    noPaymentTileOpen && closedPaymentTiles.includes('bank_transfer');

  // Check if cart is empty
  const isCartEmpty = !cart?.products || cart.products.length === 0;
  console.log('🛒 Cart status:', { 
    hasCart: !!cart, 
    hasProducts: !!cart?.products, 
    productsLength: cart?.products?.length, 
    isCartEmpty 
  });

  // Helper function to convert prices for display
  const getConvertedPrice = (originalPrice: number, originalCurrency: string, useConversion = true) => {
    if (!useConversion || originalCurrency === selectedCurrency || originalPrice === 0) {
      return { price: originalPrice, currency: selectedCurrency };
    }
    
    // For now, return the original price with selected currency
    // In a real implementation, you'd use the conversion rate
    return { price: originalPrice, currency: selectedCurrency };
  };

  const handleQuantityChange = (itemId: string, value: number, productIndex: number) => {
    if (value > 0) {
      updateItemQuantity(itemId, value, productIndex);
    }
  };

  const calculateSubtotal = () => {
    return getTotalPrice();
  };

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Handle email change and save to localStorage and update cart
  const changeEmail = async (newEmail: string) => {
    setEmail(newEmail);
    setEmailError('');
    setEmailSaved(false);

    // Save to localStorage if it's a valid email, but only on client side
    if (validateEmail(newEmail) && typeof window !== 'undefined') {
      localStorage.setItem('userEmail', newEmail);
      setEmailSaved(true);
      identifyPosthogUser(newEmail);

      // Update cart email using cart service if we have a cartId
      if (cartId && (!cart?.email || cart.email !== newEmail)) {
        try {
          await cartService.updateCartDetails({
            cartId: cartId,
            email: newEmail
          });
          console.log('✅ Cart email updated successfully:', newEmail);
        } catch (error) {
          console.error('❌ Failed to update cart email:', error);
          // Don't show error to user as this is a background operation
        }
      }

      // Hide success message after 3 seconds
      setTimeout(() => {
        setEmailSaved(false);
      }, 3000);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => changeEmail(e.target.value);

  // Handle gift email change
  const changeGiftEmail = (newGiftEmail: string) => {
    setGiftEmail(newGiftEmail);
    setGiftEmailError('');
  };

  const handleGiftEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => changeGiftEmail(e.target.value);

  // A mistyped domain (gmail.con, gmial.com) would send the codes nowhere.
  // The account's verified address is proven, so it is not asked about.
  const isProvenAccountEmail = !!profile?.isEmailVerified && profile.email === email.trim();
  const emailCheck = useEmailCheck({
    cartService,
    email,
    active: isMounted && validateEmail(email.trim()) && !isProvenAccountEmail,
    onAccept: changeEmail,
  });
  const giftEmailCheck = useEmailCheck({
    cartService,
    email: giftEmail,
    active: buyAsGift && validateEmail(giftEmail.trim()),
    onAccept: changeGiftEmail,
  });

  // Handle gift message change
  const handleGiftMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setGiftMessage(e.target.value);
  };

  // Ref-based synchronous guard. `orderLoading` from useOrdersService flips
  // via setState which is async - between the click handler returning and the
  // next render, a second click can slip through. A ref updates immediately.
  const checkoutInFlightRef = useRef(false);

  const handleCheckout = async () => {
    if (checkoutInFlightRef.current) {
      console.warn('🛑 Checkout already in flight, ignoring duplicate click');
      return;
    }
    checkoutInFlightRef.current = true;
    try {
      // Fresh attempt, fresh slate: a resolved payer error (name fixed via
      // blur-save) would otherwise linger through a successful checkout.
      setPayerGateError(null);

      if (isCartEmpty) {
        throw new Error('Cart is empty');
      }

      if (!email) {
        setEmailError('Email is required');
        return;
      }

      if (!validateEmail(email)) {
        setEmailError('Please enter a valid email address');
        return;
      }

      // Validate gift email if buying as gift
      if (buyAsGift) {
        if (!giftEmail) {
          setGiftEmailError('Gift recipient email is required');
          return;
        }
        if (!validateEmail(giftEmail)) {
          setGiftEmailError('Please enter a valid gift recipient email address');
          return;
        }
      }

      // SEPA: the payer form must be complete BEFORE the request goes out -
      // the backend refuses to surface any OTP challenge until it is, and
      // this guard also covers the post-OTP retry effect, which bypasses the
      // button's click validation.
      if (
        paymentMethod === 'bank_transfer' &&
        !balanceFullyCovers &&
        !payerBankAccountId
      ) {
        setPayerGateError({ errorCode: 'RISK_SEPA_BANK_ACCOUNT_REQUIRED' });
        return;
      }

      // A domain that takes no mail, or a likely typo not answered yet: the
      // notice under the field says which. Last of the guards, because it
      // may have to wait for the API.
      if ((await emailCheck.stopsCheckout()) || (await giftEmailCheck.stopsCheckout())) {
        return;
      }

      // Save valid email to localStorage before checkout (client-side only)
      if (typeof window !== 'undefined') {
        localStorage.setItem('userEmail', email.trim());
      }
      setEmailError('');

      if (!cartId) {
        throw new Error('No cart found. Please add items to cart first.');
      }

      const gaClientId = getGaClientId();
      const gaSessionId = getGaSessionId();
      const gclid = getGclid();
      const fbclid = getFbclid();
      const fbp = getFbp();
      const fbc = getFbc();

      const posthogDistinctId = getPosthogDistinctId();
      const attribution = getStoredAttribution();

      const orderData = {
        email: email.trim(),
        currency: selectedCurrency,
        cartId: cartId,
        paymentMethod: paymentMethod,
        ...(paymentMethod === 'crypto' && { payOption }),
        ...(couponCode.trim() && { couponCode: couponCode.trim() }),
        ...(buyAsGift && giftEmail && {
          giftEmail: giftEmail.trim(),
          ...(giftMessage.trim() && { giftMessage: giftMessage.trim() })
        }),
        ...(subscribeToNewsletter && { subscribeToNewsletter: true }),
        ...(gaClientId && { gaClientId }),
        ...(gaSessionId && { gaSessionId }),
        ...(gclid && { gclid }),
        ...(fbclid && { fbclid }),
        ...(posthogDistinctId && { posthogDistinctId }),
        ...(attribution && { attribution }),
        ...(fbp && { fbp }),
        ...(fbc && { fbc }),
        marketingConsent: consentCategories.marketing,
        // Recorded on the order: consent to immediate performance is what
        // lets us start before the withdrawal period expires, so it has to be
        // provable after the fact, not just enforced in the UI.
        withdrawalConsent: expressConsent,
        ...(balanceApplyCents > 0 && { balanceApplyCents }),
        // Deliberately NOT gated on balanceFullyCovers: the server clamps
        // balanceApplyCents and can reintroduce a residual - a selected
        // account lets that order proceed instead of bouncing on the gate.
        ...(paymentMethod === 'bank_transfer' &&
          payerBankAccountId && { payerBankAccountId }),
        ...(turnstile.token && { turnstileToken: turnstile.token }),
      };

      // Meta Pixel InitiateCheckout - fire fbq browser-side, capture the
      // eventId, and forward it on orderData so the server fires CAPI with the
      // same id (Meta dedupes by event_id).
      const checkoutContentIds = Array.from(new Set((cart?.products ?? []).map((p) => p.productId)));
      const checkoutNumItems = (cart?.products ?? []).reduce((sum, p) => sum + (p.quantity || 0), 0);
      const checkoutValue = cartPricing?.total ?? getTotalPrice();
      const checkoutCurrency = cartPricing?.currency ?? selectedCurrency;
      const metaEventId = checkoutContentIds.length > 0 && checkoutValue
        ? trackInitiateCheckout(checkoutValue, checkoutCurrency, checkoutContentIds, checkoutNumItems) ?? undefined
        : undefined;
      if (metaEventId) {
        (orderData as typeof orderData & { metaEventId?: string }).metaEventId = metaEventId;
      }
      if (checkoutValue) {
        trackGaEcommerce(GaEcommerceEvent.BeginCheckout, checkoutCurrency, checkoutValue, gaCartItems());
      }

      console.log('🛒 Creating order with data:', orderData);
      const response = await createOrder(orderData);
      console.log('📦 Order response:', response);

      // Balance was reserved server-side; refresh the cached balance so the
      // account page + next cart session see the new value.
      if (balanceApplyCents > 0 && profile?._id) {
        refreshBalance().catch(() => undefined);
      }

      if (response?.orderId) {
        if (typeof window !== 'undefined') {
          localStorage.setItem('pendingOrderId', response.orderId);
        }

        // Route off the server-assigned provider, not the locally-selected
        // paymentMethod: a bank-transfer request that was 100% covered by
        // balance comes back as 'internal_balance' and must go straight to
        // success - not to the bank page (no wire is owed).
        const assignedProvider = response.paymentProvider;

        if (assignedProvider === 'internal_balance') {
          console.log('💰 Full-balance order - going to success page:', response.orderId);
          router.push(`/order/success?id=${response.orderId}`);
        } else if (assignedProvider === 'bank_transfer') {
          console.log('🏦 Bank transfer - redirecting to:', `/order/bank/${response.orderId}`);
          router.push(`/order/bank/${response.orderId}`);
        } else if (response.redirectUrl) {
          window.location.href = response.redirectUrl;
        } else {
          // Server sent no provider and no redirectUrl - shouldn't happen,
          // but fall back to success rather than declined since the order
          // exists.
          console.warn('⚠️ No paymentProvider or redirectUrl in response; falling back to success');
          router.push(`/order/success?id=${response.orderId}`);
        }
      } else {
        console.error('❌ No orderId in response:', response);
        throw new Error('No order ID received from order creation');
      }
    } catch (error) {
      console.error('💥 Order creation error:', error);
      turnstile.reset();
      const errorCode = (error as { errorCode?: string })?.errorCode;
      const detailsRaw = (error as { details?: Record<string, unknown> })?.details;

      // An amount rule demands a step a guest cannot pass: keep the shopper
      // on the cart with the login notice instead of the declined page.
      if (errorCode === 'RISK_ORDER_LOGIN_REQUIRED') {
        setAmountRuleLoginRequired(true);
        return;
      }
      // Risk-band step-ups are not declines - the order is allowed once the
      // user proves the channel. Open the OTP modal; on success it simply
      // re-runs handleCheckout (fresh confirm satisfies the band server-side).
      if (errorCode === 'RISK_ORDER_OTP_REQUIRED') {
        setOtpChallenge({
          mode: 'codes',
          emailRequired: !!(detailsRaw as { emailOtpRequired?: boolean })?.emailOtpRequired,
          phoneRequired: !!(detailsRaw as { phoneOtpRequired?: boolean })?.phoneOtpRequired,
        });
        return;
      }
      if (errorCode === 'RISK_SEPA_PHONE_VERIFICATION_REQUIRED') {
        setOtpChallenge({ mode: 'phoneVerify', emailRequired: false, phoneRequired: true });
        return;
      }
      // Identity verification (KYC) rung: the modal sends the shopper to the
      // hosted check and polls until approved, then retries like the OTPs.
      if (errorCode === 'RISK_ORDER_KYC_REQUIRED') {
        const kycDetails = detailsRaw as { kycStatus?: KycStatus; retryable?: boolean } | undefined;
        setKycChallenge({
          kycStatus: kycDetails?.kycStatus ?? KycStatus.None,
          retryable: kycDetails?.retryable,
        });
        return;
      }

      // The API refused an address the cart had let through (its own
      // question failed, or the address is the account's): fixable right
      // here, so it is shown at the field and not on /order/declined.
      if (errorCode === 'GIFT_EMAIL_DOMAIN_NOT_FOUND') {
        const refused = detailsRaw as { email?: string; suggestion?: string } | undefined;
        const isGiftEmail = buyAsGift && refused?.email !== email.trim();
        (isGiftEmail ? giftEmailCheck : emailCheck).refuse(refused?.suggestion);
        return;
      }

      // Payer-detail gaps are recoverable right here on the cart (missing
      // name, stale/deleted account) - surface them at the payer panel
      // instead of the /order/declined dead end.
      if (
        errorCode === 'RISK_SEPA_NAME_REQUIRED' ||
        errorCode === 'RISK_SEPA_BANK_ACCOUNT_REQUIRED' ||
        errorCode === 'BANK_ACCOUNT_NOT_FOUND'
      ) {
        setPayerGateError(error);
        return;
      }

      const params = new URLSearchParams();
      if (errorCode) params.set('code', errorCode);
      if (detailsRaw) params.set('details', JSON.stringify(detailsRaw));
      router.push(`/order/declined?${params.toString()}`);
    } finally {
      checkoutInFlightRef.current = false;
    }
  };

  // OTP step-up modal driver: verified → close, then retry the checkout once
  // turnstile has a token again. reset() nulled it when the challenged
  // attempt failed, and the retry bypasses the button's isFormValid gate - a
  // tokenless submit would bounce off the guard onto /order/declined.
  const handleOtpVerified = () => {
    setOtpChallenge(null);
    setPendingOtpRetry(true);
  };

  // KYC modal driver: same retry path as the OTPs. The profile refresh pulls
  // the new kycStatus for the account badge; the checkout itself does not
  // depend on it (the backend re-checks the verification rows).
  const handleKycVerified = () => {
    setKycChallenge(null);
    void refreshProfile().catch(() => undefined);
    setPendingOtpRetry(true);
  };

  useEffect(() => {
    if (!pendingOtpRetry) return;
    if (turnstile.isRequired && !turnstile.token) return;
    setPendingOtpRetry(false);
    void handleCheckout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingOtpRetry, turnstile.isRequired, turnstile.token]);

  // Handle cart recovery from URL. adoptCart (not loadCart) so the URL cart
  // beats the localStorage cart this browser may already have.
  const loadCartFromUrl = async (urlCartId: string) => {
    try {
      console.log('🔄 Loading cart from URL, cart ID:', urlCartId);
      await adoptCart(urlCartId);
      console.log('✅ Cart loaded successfully from URL');
    } catch (error) {
      console.error('❌ Failed to load cart from URL:', error);
    }
  };

  // Handle product navigation
  const handleProductClick = (card: Card) => {
    if (card?.slug) {
      const pathname = window.location.pathname;
      const pathSegments = pathname.split('/').filter(Boolean);
      const currentLocale = (pathSegments[0] && locales.includes(pathSegments[0] as Locale) 
        ? pathSegments[0] 
        : defaultLocale) as Locale;
      
      const localizedGiftcards = getLocalizedPath('/giftcards', currentLocale);
      const productPath = `${localizedGiftcards}/${card.slug}`;
      const fullPath = currentLocale === defaultLocale ? productPath : `/${currentLocale}${productPath}`;
      router.push(fullPath);
    }
  };

  // Check if form is valid for checkout (minOrderError checked separately in disabled condition)
  // When Turnstile is configured, also require a fresh token before the Buy
  // button activates - the widget produces one within a second of mount.
  const isFormValid = !!(email && validateEmail(email) && (!buyAsGift || (giftEmail && validateEmail(giftEmail))))
    && (!turnstile.isRequired || !!turnstile.token);

  return {
    // State
    isMounted,
    cartItems,
    cartPricing,
    couponCode,
    setCouponCode,
    isLoadingPricing,
    couponError,
    setCouponError,
    pricingError,
    minOrderError,
    email,
    emailError,
    emailSaved,
    emailCheck,
    giftEmailCheck,
    expressConsent,
    setExpressConsent,
    subscribeToNewsletter,
    setSubscribeToNewsletter,
    buyAsGift,
    setBuyAsGift,
    giftEmail,
    giftEmailError,
    giftMessage,
    orderLoading,
    isCartEmpty,
    isFormValid,
    paymentMethod,
    setPaymentMethod,
    payOption,
    setPayOption,
    isAuthenticated: !!user,
    authRequiredCards,
    authRequiredBlocked,
    paymentRestrictedCards,
    blockedPaymentTiles,
    unavailablePaymentTiles: tileSwitches.unavailable,
    hiddenPaymentTiles: tileSwitches.hidden,
    paymentTilesReady,
    paymentNeedsLogin,
    noPaymentPossible,
    amountRuleLoginRequired,
    balanceApplyCents,
    setBalanceApplyCents,
    userBalanceCents: balanceCents ?? 0,
    isEmailVerified: !!profile?.isEmailVerified,
    cartError,
    cartErrorCode,
    clearCartError,

    // Risk-band OTP step-up modal
    otpChallenge,
    closeOtpChallenge: () => setOtpChallenge(null),
    handleOtpVerified,

    // Identity-verification (KYC) step-up modal
    kycChallenge,
    closeKycChallenge: () => setKycChallenge(null),
    handleKycVerified,
    profileEmail: profile?.email,
    profilePhone: profile?.phoneNumber,

    // SEPA payer details
    payerBankAccountId,
    setPayerBankAccountId,
    payerGateError,
    balanceFullyCovers,
    setBalanceFullyCovers,
    
    // Cart context
    cart,
    cards,
    cartId,
    removeFromCart,
    updateItemQuantity,
    clearCart,
    getTotalPrice,
    isLoading,
    selectedCurrency,
    
    // Functions
    fetchBasicCartPricing,
    handleApplyCoupon,
    getConvertedPrice,
    handleQuantityChange,
    calculateSubtotal,
    validateEmail,
    handleEmailChange,
    handleGiftEmailChange,
    handleGiftMessageChange,
    handleCheckout,
    handleProductClick,
    loadCartFromUrl,

    // Cloudflare Turnstile widget - render in the Cart summary above the
    // Buy button. Null when TURNSTILE_SITE_KEY is not configured.
    turnstileWidget: turnstile.widget,
  };
};
